import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Input, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label } from 'reactstrap';
import { getCourses, createCourse } from '../../../../Api/lms/apiUtils';
import SongsList from './SongList';
import ResourcesList from './ResourcesList';
import { toast } from 'react-toastify';
import './style.scss';
import TabManager from './tab';
import PracticeMaterial from './PracticeMaterial';
import CourseTree, { TreeSelection } from './CourseTree';

// Only these emails may access the Practice Material tab.
const PRACTICE_ALLOWED_EMAILS = ['mehul@artiumacademy.com', 'pragya@artiumacademy.com','kartheek@artiumacademy.com','swati@artiumacademy.com'];

const getProfileEmail = (): string => {
  try {
    const raw = localStorage.getItem('profileData');
    if (!raw) return '';
    return (JSON.parse(raw)?.emailId || '').toLowerCase().trim();
  } catch {
    return '';
  }
};

type Course = {
  id: number;
  name: string;
  levels: string[];
  course_name: string[];
  lessons: {
    level: string;
    topics: {
      name: string;
      resources: {
        type: string;
        name: string;
        course_name: string[];
        levels: string[];
      }[];
    }[];
  }[];
};

function CourseList() {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [selectedLevelDesc, setSelectedLevelDesc] = useState<string[]>([]);
  const [selectedLevel, setSelectedLevel] = useState('');
  const [newCourse, setNewCourse] = useState({ name: '', level: [], lessons: 0 });
  const [courses, setCourses] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [panelCollapsed, setPanelCollapsed] = useState<boolean>(false);

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const BASE = process.env.PUBLIC_URL || '';
  const courseIdOf = (c: any) => c?.course_id ?? c?.id;

  const canAccessPractice = PRACTICE_ALLOWED_EMAILS.includes(getProfileEmail());

  // The tree selection is derived from the URL (URL is the source of truth).
  const selection: TreeSelection = React.useMemo(() => {
    if (pathname.includes('/resources')) return { type: 'resources' };
    if (pathname.includes('/songs')) return { type: 'songs' };
    if (pathname.includes('/practice')) return { type: 'practice' };
    const m = pathname.match(/\/courses\/([^/]+)/);
    if (m) {
      const c = courses.find((x) => String(courseIdOf(x)) === decodeURIComponent(m[1]));
      if (c) return { type: 'course', course: c };
    }
    return null;
  }, [pathname, courses]);

  const courseLevels = [
    { id: 1, name: 'Preparatory' },
    { id: 2, name: 'Intermediate' },
    { id: 3, name: 'Intermediate 1' },
    { id: 4, name: 'Intermediate 2' },
    { id: 5, name: 'Proficient' },
    { id: 6, name: 'Advanced' },
  ];

  const fetchCourses = async () => {
    setIsLoading(true);
    try {
      const response = await getCourses();
      if (response.courses) {
        setCourses(response.courses);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setNewCourse({ name: '', level: [], lessons: 0 });
    setSelectedLevels([]);
    setSelectedLevelDesc([]);
  };

  React.useEffect(() => {
    fetchCourses();
  }, []);

  const toggleNewCourseModal = () => setModalOpen(!modalOpen);

  const handleAddCourse = async () => {
    let levelParam = [] as any;
    courseLevels.forEach((level) => {
      if (selectedLevels.includes(level.name)) {
        levelParam.push({
          levelName: level.name,
          description:
            ((selectedLevelDesc || []).find((v) => (v as any).levelName == level.name) as any)?.description || '',
        });
      }
    });
    let reqParam = {
      courseName: newCourse.name,
      description: (newCourse as any).description || '',
      levels: levelParam,
    };
    try {
      const response = await createCourse(reqParam);
      if (response?.course?.course_id) {
        resetForm();
        toggleNewCourseModal();
        fetchCourses();
        toast.success((response as any).message);
      } else {
        alert('Failed to add course:');
      }
    } catch (error) {
      console.error('Error adding course:', error);
    }
  };

  // Tree selection drives the URL.
  const handleTreeSelect = (next: TreeSelection) => {
    if (!next) navigate(`${BASE}/courses`);
    else if (next.type === 'resources') navigate(`${BASE}/resources`);
    else if (next.type === 'songs') navigate(`${BASE}/songs`);
    else if (next.type === 'practice') navigate(`${BASE}/practice`);
    else if (next.type === 'course') navigate(`${BASE}/courses/${courseIdOf(next.course)}`);
  };

  const selectedCourse = selection?.type === 'course' ? selection.course : null;
  const selectedCourseId = selectedCourse ? courseIdOf(selectedCourse) : null;

  // Reset the active level whenever the selected course changes.
  React.useEffect(() => {
    if (selectedCourse) {
      setSelectedLevel(selectedCourse?.levels?.[0]?.levelName || 'Prep');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourseId]);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    if (checked) {
      setSelectedLevels([...selectedLevels, value]);
    } else {
      setSelectedLevels(selectedLevels.filter((level) => level !== value));
    }
  };

  const handleCancel = () => {
    setModalOpen(!modalOpen);
    resetForm();
  };

  const renderContent = () => {
    if (!selection) {
      return (
        <div className="lms-empty-state">
          <h4>Welcome to the Course Library</h4>
          <p>Select a course, Resources, Songs, or Practice Material from the left to get started.</p>
        </div>
      );
    }

    if (selection.type === 'course') {
      return (
        <>
          <div className="lms-content__header">
            <h4 className="lms-content__title">{selectedCourse?.course_name}</h4>
          </div>
          <TabManager
            setCourses={setCourses}
            selectedCourse={selectedCourse}
            setSelectedCourse={(c: any) =>
              setCourses((prev) =>
                prev.map((pc) => (courseIdOf(pc) === courseIdOf(c) ? c : pc))
              )
            }
            selectedLevel={selectedLevel}
            courseLevels={courseLevels}
            fetchCourses={fetchCourses}
            courseDetail={selectedCourse}
          />
        </>
      );
    }

    if (selection.type === 'resources') {
      return (
        <>
          <div className="lms-content__header">
            <h4 className="lms-content__title">Resources</h4>
          </div>
          <ResourcesList />
        </>
      );
    }

    if (selection.type === 'songs') {
      return (
        <>
          <div className="lms-content__header">
            <h4 className="lms-content__title">Songs</h4>
          </div>
          <SongsList />
        </>
      );
    }

    // practice (AI practice) — restricted to a single email
    if (!canAccessPractice) {
      return (
        <div className="lms-empty-state">
          <h4>Access restricted</h4>
          <p>You don’t have permission to view Practice Material.</p>
        </div>
      );
    }

    return (
      <>
        <div className="lms-content__header">
          <h4 className="lms-content__title">Practice Material</h4>
        </div>
        <PracticeMaterial courses={courses} />
      </>
    );
  };

  return (
    <div className="page-body lms-layout">
      <aside className={`lms-layout__sidebar ${panelCollapsed ? 'is-collapsed' : ''}`}>
        <CourseTree
          courses={courses}
          isLoading={isLoading}
          selection={selection}
          onSelect={handleTreeSelect}
          onAddCourse={toggleNewCourseModal}
          collapsed={panelCollapsed}
          onToggleCollapse={() => setPanelCollapsed((prev) => !prev)}
          canAccessPractice={canAccessPractice}
        />
      </aside>

      <main className="lms-layout__content">{renderContent()}</main>

      <Modal isOpen={modalOpen} toggle={toggleNewCourseModal}>
        <ModalHeader toggle={toggleNewCourseModal}>Add New Course</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="courseName">Course Name</Label>
            <Input
              type="text"
              id="courseName"
              placeholder="Enter course name"
              value={newCourse.name}
              onChange={(e) => setNewCourse({ ...newCourse, name: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <Label for="courseLevels">Course Levels</Label>
            <div>
              {courseLevels.map((level, index) => (
                <FormGroup key={index} check>
                  <Label check>
                    <Input
                      type="checkbox"
                      value={level.name}
                      className="levelChk"
                      checked={selectedLevels.includes(level.name)}
                      onChange={handleCheckboxChange}
                    />
                    {level.name}
                  </Label>
                </FormGroup>
              ))}
            </div>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleAddCourse}>
            Save Course
          </Button>
          <Button color="secondary" onClick={handleCancel}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default CourseList;
